import { DynamicForm } from "@shared/components/DynamicForm";
import { LocationMap } from "@shared/components/LocationMap";
import { StepHelpers } from "@shared/components/Stepper";
import { toast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useAssetClassList,
  useAssetDetail,
  useAssetStatusList,
  useAssetTypeList,
  useCreateAsset,
  useDepreciationMethodList,
  useFacilitiesList,
  useGetAssets,
  useGetManuFacturer,
  useSystemNetworkList,
  useUpdateAsset,
} from "../../hooks";

interface Step1BasicInfoProps {
  editMode: boolean;
  assetId?: string; // For edit mode
  // Props automatically passed by Stepper
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

const Step1BasicInfo: React.FC<Step1BasicInfoProps> = ({
  editMode,
  assetId,
  stepHelpers,
  currentStepIndex = 0,
  data = {},
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  setValidationError,
  clearValidationError,
  isValidating,
}) => {
  const { id, action } = useParams();
  const dataLoadedRef = useRef(false);
  const navigate = useNavigate();
  const { data: facilityList } = useFacilitiesList();
  const { data: manufacturerList } = useGetManuFacturer();
  const { data: assetList } = useGetAssets();
  const { data: assetClassList } = useAssetClassList();
  const [classCode, setClassCode] = useState("");
  const [facilityId, setFacilityId] = useState("");
  const [isFormPopulated, setIsFormPopulated] = useState(false);
  const { data: systemNetworkList } = useSystemNetworkList(facilityId);
  const { data: assetTypeList } = useAssetTypeList(classCode || null);
  const { data: assetStatusList } = useAssetStatusList();
  const { data: depreciationMethodList } = useDepreciationMethodList();

  // API mutation hooks
  const { mutateAsync: createAsset, isPending: isCreating } = useCreateAsset();
  const { mutateAsync: updateAsset, isPending: isUpdating } = useUpdateAsset(
    stepHelpers?.getStepData(0)?.assetDraftId || id
  );

  const { data: assetDetail } = useAssetDetail(id);

  const saveStepData = useCallback(
    (stepData: any) => {
      if (!stepHelpers) return;

      try {
        const dataToSave = {
          ...stepData,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, dataToSave);
      } catch (error) {
        toast({
          title: "Failed to save data",
          variant: "destructive",
        });
      }
    },
    [stepHelpers, currentStepIndex]
  );

  // Helper function to get step data using stepHelpers
  const getStepData = useCallback(() => {
    if (!stepHelpers) return {};

    try {
      return stepHelpers.getStepData(currentStepIndex) || {};
    } catch (error) {
      toast({
        title: "Failed to save data",
        variant: "destructive",
      });
      return {};
    }
  }, [stepHelpers, currentStepIndex]);

  const facilityOptions = useMemo(() => {
    return (
      facilityList?.map((facility: any) => ({
        value: facility.id,
        label: facility.facilityName,
      })) || []
    );
  }, [facilityList]);

  const manufacturerOptions = useMemo(() => {
    return (
      manufacturerList?.map((manufacturer: any) => ({
        value: manufacturer.id,
        label: manufacturer.name,
      })) || []
    );
  }, [manufacturerList]);

  const assetOptions = useMemo(() => {
    return (
      assetList?.map((asset: any) => ({
        value: asset.id,
        label: asset.assetName,
      })) || []
    );
  }, [assetList]);

  const systemNetworkOptions = useMemo(() => {
    return (
      systemNetworkList?.map((network: any) => ({
        value: `${network.id}-${network.type}`,
        label: `${network.number}-${network.name}`,
      })) || []
    );
  }, [systemNetworkList]);

  const assetClassOptions = useMemo(() => {
    return (
      assetClassList?.map((assetClass: any) => ({
        value: assetClass.code,
        label: assetClass.name,
      })) || []
    );
  }, [assetClassList]);

  const assetTypeOptions = useMemo(() => {
    return (
      assetTypeList?.map((assetType: any) => ({
        value: assetType.code,
        label: assetType.name,
      })) || []
    );
  }, [assetTypeList]);

  const assetStatusOptions = useMemo(() => {
    return (
      assetStatusList?.result?.map((status: any) => ({
        value: status.key,
        label: status.value,
      })) || []
    );
  }, [assetStatusList]);

  const depreciationMethodOptions = useMemo(() => {
    return (
      depreciationMethodList?.result?.map((method: any) => ({
        value: method.key,
        label: method.value,
      })) || []
    );
  }, [depreciationMethodList]);

  const [locationData, setLocationData] = useState<any>({
    latitude: data?.payload?.latitude,
    longitude: data?.payload?.longitude,
    address: data?.payload?.address,
  });

  useEffect(() => {
    if (!editMode && data.assetClass && data.assetType && !data.assetId) {
      const classPrefix = data.assetClass.substring(0, 3).toUpperCase();
      const typePrefix = data.assetType.substring(0, 3).toUpperCase();
      const randomNum = Math.floor(Math.random() * 1000)
        .toString()
        .padStart(3, "0");
      const newAssetId = `${classPrefix}-${typePrefix}-${randomNum}`;

      const updatedData = { ...data, assetId: newAssetId };
      onDataChange?.(updatedData);
      saveStepData(updatedData);
    }
  }, [
    data.assetClass,
    data.assetType,
    data.assetId,
    editMode,
    onDataChange,
    saveStepData,
  ]);

  const handleClassChange = (value: string) => {
    const assetClass = assetClassList?.find((c: any) => c.code === value);

    const updatedData = {
      ...data,
      selectedClassCode: value,
      assetClass: assetClass?.name || "",
      assetType: "",
      selectedTypeCode: "",
      specificFields: {},
    };
    setClassCode(value);

    onDataChange?.(updatedData);
    saveStepData(updatedData);
  };

  const handleTypeChange = (value: string) => {
    // Find the selected asset type from API data
    const assetType = assetTypeList?.find((t: any) => t.code === value);

    const updatedData = {
      ...data,
      selectedTypeCode: value,
      assetType: assetType?.name || "",
      specificFields: {},
    };

    onDataChange?.(updatedData);
    saveStepData(updatedData);
  };

  const handleFacilityChange = (value: string) => {
    setFacilityId(value);
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    switch (fieldName) {
      case "assetClass":
        handleClassChange(value);
        break;

      case "assetType":
        handleTypeChange(value);
        break;

      case "facility":
        handleFacilityChange(value);
        break;

      default:
        const updatedData = { ...data, [fieldName]: value };
        onDataChange?.(updatedData);
        saveStepData(updatedData);
        break;
    }
  };

  const handleNext = async () => {
    const isValid = await FormService.validateForm(form);
    if (!isValid) {
      if (setValidationError) {
        setValidationError("Please fill in all required fields.");
      }
      toast({
        title: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }
    const formData = form.getValues();
    const filteredFormData = Object.fromEntries(
      Object.entries(formData).filter(
        ([, value]) =>
          value !== "" &&
          value !== "0.00" &&
          value !== null &&
          value !== undefined &&
          !(Array.isArray(value) && value.length === 0)
      )
    );

    if (clearValidationError) {
      clearValidationError();
    }
    const [systemNetworkId, type] = formData.systemsNetworks?.split("-") ?? [
      "",
      "",
    ];

    if (filteredFormData.parentAsset?.[0] && filteredFormData.childAsset?.[0]) {
      if (filteredFormData.parentAsset[0] === filteredFormData.childAsset[0]) {
        toast({
          title: "Parent asset and Child asset can not be same!",
          variant: "destructive",
        });
        return;
      }
    }

    try {
      const payload = {
        asset_name: filteredFormData.asset_name,
        facility: filteredFormData.facility,
        asset_class_code: filteredFormData.assetClass,
        asset_type_code: filteredFormData.assetType,
        latitude: locationData.latitude,

        installation_year: filteredFormData.installationYear,
        serial_number: filteredFormData.serialNumber,
        useful_life: filteredFormData.modelNumber,
        manufacturer: filteredFormData.manufacturer,
        status: filteredFormData.status,
        installation_cost: filteredFormData.installationCost,
        replacement_cost: filteredFormData.replacementCost,
        depreciation_method: filteredFormData.depreciationMethod,
        parent_asset: filteredFormData.parentAsset?.[0],
        child_asset: filteredFormData.childAsset?.[0],
        remote_utility_id: getRemoteUtilityId(),
        description: filteredFormData.description,
      };
      if (locationData.latitude && locationData.longitude) {
        payload["latitude"] = locationData.latitude;
        payload["longitude"] = locationData.longitude;
      }
      if (type && systemNetworkId) {
        payload[type] = systemNetworkId;
      }

      let response;
      let assetDraftId;
      if (action === "edit") {
        response = await updateAsset(payload);
        assetDraftId =
          response?.result?.id || stepHelpers?.getStepData(0)?.id || id;
      } else {
        response = await createAsset(payload);
        assetDraftId = response?.result?.id;
      }

      if (assetDraftId) {
        const completedData = {
          ...data,
          payload,
          assetDraftId,
          response,
          isStepComplete: true,
          completedAt: new Date().toISOString(),
        };

        onDataChange?.(completedData);
        saveStepData(completedData);
        onNext?.();
        navigate(
          `/ax/asset-registry/assets/create/edit/${assetDraftId}#specific-info`
        );
      } else {
        if (setValidationError) {
          setValidationError("Failed to create asset draft. Please try again.");
        }
      }
    } catch (error) {
      toast({
        title: "Failed to submit asset data",
        variant: "destructive",
      });
      if (setValidationError) {
        setValidationError("Failed to submit. Please try again.");
      }
    }
  };

  const handlePrevious = () => {
    // Save current state before going back
    saveStepData({
      ...data,
      navigatedBack: true,
      navigatedBackAt: new Date().toISOString(),
    });

    onPrevious?.();
  };

  const handleLatitudeChange = (value: string) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < -90 || numValue > 90) {
      toast({
        title: "Invalid Latitude",
        description: "Latitude must be between -90 and 90 degrees.",
        variant: "destructive",
      });
      return;
    }
    setLocationData((prev) => ({ ...prev, latitude: numValue }));
  };

  const handleLongitudeChange = (value: string) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < -180 || numValue > 180) {
      toast({
        title: "Invalid Longitude",
        description: "Longitude must be between -180 and 180 degrees.",
        variant: "destructive",
      });
      return;
    }
    setLocationData((prev) => ({ ...prev, longitude: numValue }));
  };

  const handleLocationChange = (lat: number, lng: number) => {
    setLocationData((prev) => ({
      ...prev,
      latitude: lat,
      longitude: lng,
    }));
  };

  const handleAddressChange = (address: string) => {
    setLocationData((prev) => ({
      ...prev,
      address: address,
    }));
  };

  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "asset_name",
        label: "Asset Name",
        type: "text",
        required: true,
        placeholder: "Enter descriptive name for this asset",
        defaultValue: data.asset_name || "",
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText:
          "Enter a unique, descriptive name for this asset (e.g., 'Main Influent Pump P-102').",
      },
      {
        name: "description",
        label: "Description",
        type: "text",
        placeholder: "Enter short description for this asset",
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText: "Enter short description for this asset.",
      },
      {
        name: "facility",
        label: "Facility",
        type: "select",
        placeholder: "Select facility",
        options: facilityOptions,
        defaultValue: data.facility || "",
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText: "Select the primary facility where this asset is located.",
      },
      {
        name: "systemsNetworks",
        label: "Systems / Networks",
        type: "select",
        required: false,
        placeholder: "Select system/network",
        options: systemNetworkOptions,
        defaultValue: data.systemsNetworks || "",
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText:
          "Optional. Associate this asset with a specific system or network within the selected facility.",
      },
      {
        name: "assetClass",
        label: "Asset Class",
        type: "select",
        required: true,
        placeholder: "Select asset class",
        options: assetClassOptions,
        defaultValue: data.selectedClassCode || "",
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText:
          "Select the high-level classification for this asset. This will filter the available Asset Types.",
      },
      {
        name: "assetType",
        label: "Asset Type",
        type: "select",
        required: true,
        placeholder: "Select asset type",
        disabled: !data.selectedClassCode || assetTypeOptions.length === 0,
        options: assetTypeOptions,
        defaultValue: data.selectedTypeCode || "",
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText:
          "Select the specific type of asset. This choice determines the specific data and assessment parameters required.",
        helperText: "Please select Asset Class first to see available types.",
      },
      {
        name: "location",
        label: "Location",
        type: "text",
        placeholder: "Enter physical location",
        defaultValue: data.location || "",
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText: "Enter the specific physical location of the asset.",
        className: "col-span-2",
        customRender: () => (
          <div className="space-y-4">
            {/* Coordinate Input Fields */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Latitude
                </label>
                <Input
                  type="number"
                  step="any"
                  placeholder="e.g., 40.7128"
                  value={locationData.latitude || ""}
                  onChange={(e) => handleLatitudeChange(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Longitude
                </label>
                <Input
                  type="number"
                  step="any"
                  placeholder="e.g., -74.0060"
                  value={locationData.longitude || ""}
                  onChange={(e) => handleLongitudeChange(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Address
                </label>
                <Input
                  type="text"
                  placeholder="Enter manually or select on map"
                  value={locationData.address || ""}
                  className="w-full bg-gray-50 "
                  onChange={(e) => handleAddressChange(e.target.value)}
                />
              </div>
            </div>

            {/* Map Component */}
            <LocationMap
              latitude={locationData.latitude}
              longitude={locationData.longitude}
              onLocationChange={handleLocationChange}
              onAddressChange={handleAddressChange}
            />
          </div>
        ),
      },
      {
        name: "installationYear",
        label: "Installation Year",
        type: "number",
        min: 1900,
        max: new Date().getFullYear(),
        defaultValue: data.installationYear || new Date().getFullYear(),
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText:
          "The year the asset was originally installed and commissioned.",
      },
      {
        name: "serialNumber",
        label: "Serial Number",
        type: "text",
        required: false,
        placeholder: "Enter serial number",
        defaultValue: data.serialNumber || "",
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText:
          "The manufacturer's serial number for this asset, if available.",
      },
      {
        name: "modelNumber",
        label: "Useful Life (Years)",
        type: "number",
        placeholder: "Enter useful life in years",
        defaultValue: data.modelNumber || "",
        min: 1,
        max: 100,
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText:
          "The expected operational lifespan of this asset in years.",
      },
      {
        name: "manufacturer",
        label: "Manufacturer",
        type: "select",
        placeholder: "Enter manufacturer",
        defaultValue: data.manufacturer || "",
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText: "The company that manufactured this asset.",
        options: manufacturerOptions,
      },
      {
        name: "status",
        label: " Status",
        type: "select",
        placeholder: "Select Status",
        options: assetStatusOptions,
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText: "The current status of the asset.",
      },
      {
        name: "installationCost",
        label: "Installation Cost ($)",
        type: "number",
        required: false,
        min: 0,
        step: 0.01,
        defaultValue: data.installationCost || 0,
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText:
          "The total initial cost to purchase and install this asset.",
      },
      {
        name: "replacementCost",
        label: "Replacement Cost ($)",
        type: "number",
        required: false,
        min: 0,
        step: 0.01,
        defaultValue: data.replacementCost || 0,
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText:
          "The estimated current cost to replace this asset with a new one.",
      },
      {
        name: "depreciationMethod",
        label: "Depreciation Method",
        required: false,
        type: "select",
        placeholder: "Select method",
        options: depreciationMethodOptions,
        defaultValue: data.depreciationMethod || "",
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText:
          "The accounting method used to calculate depreciation for this asset.",
      },
      {
        name: "parentAsset",
        label: "Parent Asset",
        type: "multi-select",
        options: assetOptions,
        searchable: true,
        clearable: true,
        maxDisplayItems: 3,
        closeOnSelect: false,
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText: "Parent asset this current asset belongs to.",
        maxItems: 1,
        defaultValue: data.parentAsset || [],
        maxSelection: 1,
      },
      {
        name: "childAsset",
        label: "Child Asset",
        type: "multi-select",
        options: assetOptions,
        searchable: true,
        clearable: true,
        closeOnSelect: false,
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText: "Child assets that belong to this asset",
        maxSelection: 1,
        defaultValue: data.childAsset || [],
      },
    ],
    [
      data,
      assetDetail,
      assetClassOptions,
      assetTypeOptions,
      assetList,
      locationData,
      systemNetworkOptions,
    ]
  );

  const form = FormService.createForm({
    fields: formFields,
    defaultValues: data,
    mode: "onChange",
    shouldUnregister: true,
  });

  useEffect(() => {
    if (
      action === "edit" &&
      assetDetail &&
      !isFormPopulated &&
      assetClassOptions &&
      assetTypeOptions &&
      assetStatusOptions &&
      facilityOptions &&
      systemNetworkOptions
    ) {
      const assetData = {
        asset_name: assetDetail?.assetName,
        description: assetDetail?.description,
        facility: assetDetail?.facilityId,
        systemsNetworks: assetDetail?.system
          ? `${assetDetail.system}-system`
          : assetDetail?.network
          ? `${assetDetail.network}-network`
          : null,
        assetClass: assetDetail?.assetClassCode,
        assetType: assetDetail?.assetTypeCode,
        installationYear: assetDetail?.installationYear,
        serialNumber: assetDetail?.serialNumber,
        modelNumber: assetDetail?.usefulLife,
        manufacturer: assetDetail?.manufacturer,
        status: assetDetail?.status,
        installationCost: assetDetail?.installationCost,
        replacementCost: assetDetail?.replacementCost,
        depreciationMethod: assetDetail?.depreciationMethod,
        parentAsset: [assetDetail?.parentAsset],
        childAsset: [assetDetail?.childAsset],
      };
      onDataChange?.(assetData);
      setClassCode(assetDetail?.assetClassCode);
      setLocationData({
        latitude: assetDetail?.latitude,
        longitude: assetDetail?.longitude,
        address: assetDetail?.location,
      });
      try {
        FormService.populateForm(form, assetData, formFields);
      } catch (error) {
        toast({
          title: "Error Occured",
          variant: "destructive",
        });
      }
      setIsFormPopulated(true);
    }
  }, [
    action,
    id,
    assetDetail,
    assetClassOptions,
    assetTypeOptions,
    assetList,
    locationData,
    assetStatusOptions,
    systemNetworkOptions,
    assetStatusOptions,
    facilityOptions,
    form,
  ]);
  return (
    <div className="bg-white p-4">
      <div className="mb-3">
        <p className="page-title">Asset Identification</p>
        <p className="page-subtitle mt-2">
          Provide basic identification and categorization information for the
          asset. Fields marked with * are required.
        </p>
      </div>

      <DynamicForm
        fields={formFields}
        form={form}
        onFieldChange={handleFieldChange}
        gridColumns={2}
        config={{
          layout: "grid",
          columns: 2,
          showSubmitButton: false,
          showResetButton: false,
          spacing: "medium",
        }}
      />

      <div className="flex justify-between mt-6 pt-6 border-t border-gray-200">
        <Button
          disabled={isFirstStep}
          onClick={handlePrevious}
          variant="outline"
        >
          Previous
        </Button>
        <Button onClick={handleNext} isLoading={isCreating || isUpdating}>
          Continue to Next Step
        </Button>
      </div>
    </div>
  );
};

export default Step1BasicInfo;
